import React from 'react';
import type { DayItemProps } from './types';
import { getWeekdayName, parseDate, isToday, isWeekend } from './utils';

interface DayItemWithIndexProps extends DayItemProps {
  index?: number;
}

/**
 * 日付アイテム（デザイン重視、セル本体も含む）
 *
 * @param dateString 日付文字列（YYYY/MM/DD形式）
 * @param status 空き状況シンボル
 * @param isFirstItem 最初のアイテムかどうか
 * @param showWeekSeparator 週の区切りを表示するかどうか
 * @param index カレンダー内でのインデックス
 */
const DayItem: React.FC<DayItemWithIndexProps> = ({
  dateString,
  status,
  isFirstItem = false,
  showWeekSeparator = false,
  index = 0,
  isList = false
}) => {
  // 日付情報のパース
  const date = parseDate(dateString);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const weekday = getWeekdayName(date);
  const isTodayFlag = isToday(date);
  const isWeekendFlag = isWeekend(date);

  // アイコン種別を記号に変換
  let iconChar = '';
  let iconClassName = '';
  let loadingClass = '';
  
  switch (status) {
    case 'open':
      iconChar = '〇';
      iconClassName = 'text-brand-primary';
      break;
    case 'average':
      iconChar = '△';
      iconClassName = 'text-brand-primary';
      break;
    case 'few':
      iconChar = '×';
      iconClassName = 'text-gray-400';
      break;
    case 'not-open':
      iconChar = '×';
      iconClassName = 'text-gray-400';
      break;
    case 'loading':
      iconChar = '';
      iconClassName = '';
      loadingClass = 'animate-pulse';
      break;
    default:
      iconChar = '▲';
      iconClassName = 'text-gray-400';
      break;
  }

  // 曜日色
  let divClassName = '';
  if (date.getDay() === 0) divClassName = '!text-red-500';
  else if (date.getDay() === 6) divClassName = '!text-blue-500';

  // 今日強調
  let divClassNameOverride = '';
  if (isTodayFlag && index !== 0) divClassNameOverride = 'font-bold underline';

  // 日付表示条件
  // 週の初め(index % 7 === 0)、2週目の初め(index === 7)、1日(day === 1) の場合は月を付与
  let text1: React.ReactNode = '';
  if (index % 7 === 0 || index === 7 || day === 1) {
    text1 = <><span className="text-12">{month}/</span>{day}</>;
  } else {
    text1 = `${day}`;
  }

  // 7番目以降の要素にborder-tを付与（モバイル2行目以降）、PCはborder-tなし
  const borderTClass = (typeof index === 'number' && index >= 7) ? 'border-t md:border-t-0' : '';

  const listClassName = isList ? 'w-[calc(100%/7)]' : 'w-[calc(100%/7)] md:w-[calc(100%/14)]';

  return (
    <div
      className={`${listClassName} flex flex-col items-center bg-surfacebaseprimary border-l [border-left-style:solid] border-[#d9d9d9] ${borderTClass} ${isFirstItem ? 'border-l' : ''}`}
    >
      <div className="flex flex-col items-center gap-2 px-0 py-0.5 relative self-stretch w-full flex-[0_0_auto]">
        <div
          className={`text-12 relative w-fit mt-[-1.00px] font-text-PC-body-3-regular text-text-system-iconprimary whitespace-nowrap] ${divClassName}`}
        >
          {weekday}
        </div>
      </div>

      <div className="flex flex-col items-center justify-center gap-1 px-1 py-2 relative self-stretch w-full flex-[0_0_auto] border-t [border-top-style:solid] border-[#d9d9d9]">
        <div
          className={`relative w-fit mt-[-1.00px] font-text-PC-body-4-bold text-text-system-iconprimary whitespace-nowrap ${divClassNameOverride}`}
        >
          {text1}
        </div>

        <div className="inline-flex flex-col items-start relative flex-[0_0_auto]">
          <div className="inline-flex flex-col items-center justify-center relative flex-[0_0_auto]">
            {status === 'loading' ? (
              <div className={`relative ${loadingClass}`}>
                <div className="w-6 h-6 flex items-center justify-center">
                  <div className="absolute w-3 h-3 bg-brand-primary rounded-full animate-ping opacity-75" />
                  <div className="relative w-4 h-4 bg-gray-200 rounded-full animate-pulse" />
                </div>
              </div>
            ) : (
              <div
                className={`relative w-fit mt-[-1.00px] font-icon-fill2-0-system-icongreen1tyle:var(--icon-fill2-0-font-style)] ${iconClassName}`}
              >
                {iconChar}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DayItem;